/**
 * Secure Access → App Connectors → detail (design §10.4, §7.3).
 *
 * Tabs (VSNP-1220): **Overview** (identity, per-component health from the last
 * heartbeat, certificate, node-reported spec, host load, served apps, configuration, danger zone) · **Traffic** (per-app
 * flows, dial success rate, dial latency, bytes) · **Logs**.
 *
 * The connector is where "the user was allowed and still could not reach the
 * app" is answered: the gateway records what it authorised, only the connector
 * records whether the app actually answered. That is why the dial-result split
 * (`ok` / `refused` / `timeout` / `dns` / `unreachable`) gets a panel of its own
 * rather than a single success percentage.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AppWindow, ArrowLeft, Boxes, CheckCircle2, RefreshCw } from 'lucide-react';
import { z } from 'zod';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ZtnaChip from '../../components/access/ZtnaChip';
import AccessFormField from '../../components/access/AccessFormField';
import {
  CertificateCard,
  ComponentHealthCard,
  DeviceSpecCard,
  Field,
  MintTokenSection,
} from '../../components/access/NodeDetailBits';
import { fmtDate, nodeStatusLabel } from '../../components/access/nodeStatus';
import { QueryChart, QueryStat, QueryTable } from '../../components/observability/QueryPanels';
import { NodeLogsPanel } from '../../components/observability/NodeLogsPanel';
import { TimeRange } from '../../components/observability/TimeRange';
import { resolveTimeRange, DEFAULT_TIME_RANGE, type TimeRangeValue } from '../../utils/timeRange';
import { useToast } from '../../components/common/ToastContainer';
import { zodResolver } from '../../utils/zodResolver';
import accessService, { type AccessApp, type AppConnector } from '../../services/access.service';

const TABS = ['overview', 'traffic', 'logs'] as const;
type Tab = (typeof TABS)[number];

/** Node health + host load for a connector (design §7.3). */
const ConnectorOverviewPanels = ({ node, range }: { node: string; range: TimeRangeValue }) => (
  <>
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <QueryStat
        title="Connected to gateway"
        query={`max(acn_connected{node="${node}"})`}
        format={(v) => (v === null ? '—' : v >= 1 ? 'yes' : 'no')}
        testId="cn-stat-connected"
      />
      <QueryStat title="Apps served" query={`max(acn_apps_served{node="${node}"})`} testId="cn-stat-apps" />
      <QueryStat
        title="Gateway session age"
        query={`max(acn_gateway_session_age_seconds{node="${node}"})`}
        format={(v) => (v === null ? '—' : v > 3600 ? `${(v / 3600).toFixed(1)} h` : `${Math.round(v / 60)} min`)}
        testId="cn-stat-age"
      />
      <QueryStat
        title="Reconnects (total)"
        query={`max(acn_reconnects_total{node="${node}"})`}
        testId="cn-stat-reconnects"
        hint="since the process started"
      />
    </div>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <QueryChart title="CPU usage" query={`node_cpu_usage_percent{node="${node}"}`} range={range} unit="%" testId="cn-chart-cpu" />
      <QueryChart
        title="Memory used"
        query={`100 * node_memory_used_bytes{node="${node}"} / node_memory_total_bytes`}
        range={range}
        unit="%"
        testId="cn-chart-mem"
      />
    </div>
  </>
);

/** Traffic tab (design §7.3). */
const ConnectorTrafficTab = ({ node, range }: { node: string; range: TimeRangeValue }) => {
  // Every rate follows the picker (doc 22 §10.4).
  const win = resolveTimeRange(range).window;
  return (
  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <QueryChart
      title="Flows active by app"
      query={`acn_app_flows_active{node="${node}"}`}
      range={range}
      labelKeys={['app', 'proto']}
      testId="cn-chart-flows"
    />
    <QueryChart
      title="Bytes per second by app"
      query={`sum by (app, direction) (rate(acn_app_bytes_total{node="${node}"}[${win}]))`}
      range={range}
      labelKeys={['app', 'direction']}
      unit=" B/s"
      testId="cn-chart-bytes"
    />
    <QueryChart
      title="Dial success rate (%)"
      // As a PERCENTAGE, not a bare 0–1 (doc 22 §10.11): every panel beside it
      // is a percentage, and a lone ratio invites being read as a count.
      query={`100 * sum by (app) (rate(acn_app_dial_total{node="${node}", result="ok"}[${win}])) / sum by (app) (rate(acn_app_dial_total{node="${node}"}[${win}]))`}
      range={range}
      labelKeys={['app']}
      unit="%"
      testId="cn-chart-dialrate"
    />
    <QueryChart
      title="Dial latency p95"
      query={`histogram_quantile(0.95, sum by (app, le) (rate(acn_app_dial_latency_seconds_bucket{node="${node}"}[${win}])))`}
      range={range}
      labelKeys={['app']}
      unit=" s"
      testId="cn-chart-diallatency"
    />
    <QueryTable
      title="Dial results (per second)"
      query={`sum by (result) (rate(acn_app_dial_total{node="${node}"}[${win}]))`}
      labelKey="result"
      valueLabel="rate/s"
      format={(v) => v.toFixed(4)}
      emptyMessage={`No dials in the last ${win}.`}
      testId="cn-dial-results"
    />
    <QueryTable
      title="Unknown-app flows"
      query={`acn_unknown_app_flows_total{node="${node}"}`}
      labelKey="node"
      valueLabel="total"
      emptyMessage="No flows arrived for an app this connector does not serve."
      testId="cn-unknown-app"
    />
  </div>
  );
};

const configSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  connectorGroup: z.string().min(1, 'Connector group is required').max(128),
  site: z.string().max(128).optional(),
  description: z.string().max(512).optional(),
  releaseChannel: z.enum(['stable', 'canary', 'beta']),
});

type ConfigForm = z.infer<typeof configSchema>;

const ConnectorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as Tab) : 'overview';
  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);

  const [connector, setConnector] = useState<AppConnector | null>(null);
  const [servedApps, setServedApps] = useState<AccessApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [decommissioning, setDecommissioning] = useState(false);

  const form = useForm<ConfigForm>({ resolver: zodResolver<ConfigForm>(configSchema) });
  const { reset } = form;

  const fetchConnector = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const cn = await accessService.getConnector(id);
      setConnector(cn);
      reset({
        name: cn.name ?? '',
        connectorGroup: cn.connector_group,
        site: cn.site ?? '',
        description: cn.description ?? '',
        releaseChannel: cn.release_channel,
      });
      // Served apps are DERIVED via the group (§8.3): every app targeting this
      // connector's group is served by it. Read-only here; edited on the app.
      try {
        const apps = await accessService.listApps({ connectorGroup: cn.connector_group, limit: 200 });
        setServedApps((apps?.items ?? []).filter((a) => a.enabled));
      } catch {
        setServedApps([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the connector');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    void fetchConnector();
  }, [fetchConnector]);

  const saveConfig = form.handleSubmit(async (values) => {
    if (!connector) return;
    try {
      await accessService.updateConnector(connector.id, {
        name: values.name,
        connectorGroup: values.connectorGroup,
        site: values.site || null,
        description: values.description || null,
        releaseChannel: values.releaseChannel,
      });
      toast.success('Configuration saved', connector.name ?? connector.node_id);
      await fetchConnector();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the configuration');
    }
  });

  const handleApprove = async () => {
    if (!connector) return;
    try {
      await accessService.approveConnector(connector.id);
      toast.success('Connector approved', connector.name ?? connector.node_id);
      await fetchConnector();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the connector');
    }
  };

  const handleDecommission = async () => {
    if (!connector) return;
    try {
      setDecommissioning(true);
      await accessService.decommissionConnector(connector.id);
      toast.success('Connector decommissioned', connector.name ?? connector.node_id);
      navigate('/access/connectors');
    } catch (err) {
      toast.error('Decommission failed', err instanceof Error ? err.message : 'Could not decommission the connector');
    } finally {
      setDecommissioning(false);
    }
  };

  if (loading && !connector) {
    return <div className="py-16 text-center text-gray-500">Loading connector…</div>;
  }
  if (error || !connector) {
    return (
      <div className="space-y-4">
        <Link to="/access/connectors" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> App Connectors
        </Link>
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error ?? 'Connector not found'}
        </div>
      </div>
    );
  }

  const awaitingEnrolment = !connector.certificate_serial_number && connector.status !== 'decommissioned';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/access/connectors" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
            <ArrowLeft className="h-4 w-4" /> App Connectors
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{connector.name ?? connector.node_id}</h1>
            <ZtnaChip />
            <StatusBadge status={nodeStatusLabel(connector.status, connector.certificate_serial_number)} />
          </div>
          <p className="mt-1 font-mono text-xs text-gray-500">{connector.node_id}</p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRange value={range} onChange={setRange} />
          <button type="button" className="btn-secondary" onClick={() => void fetchConnector()} disabled={loading}>
            <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {connector.status === 'pending_approval' && connector.certificate_serial_number && (
            <button type="button" className="btn-primary" onClick={() => void handleApprove()}>
              <CheckCircle2 className="mr-2 inline h-4 w-4" />
              Approve
            </button>
          )}
        </div>
      </div>

      {awaitingEnrolment && (
        <div className="card border-teal-200 bg-teal-50/40">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-900">Awaiting enrolment</h2>
              <p className="mt-0.5 text-sm text-gray-600">
                This connector is registered but has not enrolled. Generate a one-time token and run the
                bootstrap on the VM — the deployment type and hardware facts are reported by the node itself.
              </p>
            </div>
            <MintTokenSection kind="app_connector" nodeRowId={connector.id} nodeName={connector.name ?? connector.node_id} nodeId={connector.node_id} />
          </div>
        </div>
      )}

      {/* Tabs (design §7.3) */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`cn-tab-${t}`}
              onClick={() => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true })}
              className={`border-b-2 px-1 py-3 text-sm font-medium capitalize ${
                tab === t
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'traffic' && <ConnectorTrafficTab node={connector.node_id} range={range} />}
      {tab === 'logs' && <NodeLogsPanel node={connector.node_id} range={range} />}

      {tab === 'overview' && (
      <>
      <ConnectorOverviewPanels node={connector.node_id} range={range} />

      {/* Overview */}
      <div className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Boxes className="h-4 w-4 text-teal-600" /> Overview
        </h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
          <Field label="Node ID" value={connector.node_id} mono />
          <Field label="Group" value={connector.connector_group} />
          <Field label="Site" value={connector.site} />
          <Field label="Deployment" value={connector.deployment_type} />
          <Field label="Version" value={connector.version} />
          <Field label="Health" value={connector.health} />
          <Field label="Registered" value={fmtDate(connector.registered_at)} />
          <Field label="Last seen" value={fmtDate(connector.last_seen)} />
        </div>
      </div>

      {/* Per-component health from the heartbeat (app_connectors.last_components, V37) */}
      <ComponentHealthCard
        components={connector.last_components}
        reportedAt={connector.last_seen}
        stale={connector.health === 'unknown'}
      />

      <CertificateCard
        serial={connector.certificate_serial_number}
        issuedAt={connector.certificate_issued_at}
        expiresAt={connector.certificate_expires_at}
      />

      <DeviceSpecCard spec={connector.device_spec} />

      {/* Served apps — derived via group, edited on the app (§8.3) */}
      <div className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <AppWindow className="h-4 w-4 text-teal-600" /> Serves apps
          <span className="text-xs font-normal text-gray-400">via group '{connector.connector_group}' — edit on the app</span>
        </h2>
        {servedApps.length === 0 ? (
          <p className="text-sm text-gray-500">No enabled apps target this connector's group yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {servedApps.map((app) => (
              <span key={app.id} className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                <span className="font-medium">{app.display_name || app.name}</span>
                <span className="ml-1 text-gray-400">{app.app_type}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="card">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Configuration</h2>
        <div className="grid grid-cols-2 gap-4">
          <AccessFormField label="Name" htmlFor="cnd-name" required error={form.formState.errors.name?.message}>
            <input id="cnd-name" className="input-base w-full" {...form.register('name')} />
          </AccessFormField>
          <AccessFormField label="Connector Group" htmlFor="cnd-group" required error={form.formState.errors.connectorGroup?.message} hint="Moving groups changes which apps this connector serves.">
            <input id="cnd-group" className="input-base w-full" {...form.register('connectorGroup')} />
          </AccessFormField>
          <AccessFormField label="Site" htmlFor="cnd-site" error={form.formState.errors.site?.message}>
            <input id="cnd-site" className="input-base w-full" {...form.register('site')} />
          </AccessFormField>
          <AccessFormField label="Release Channel" htmlFor="cnd-channel" error={form.formState.errors.releaseChannel?.message} hint="Upgrade policy.">
            <select id="cnd-channel" className="input-base w-full" {...form.register('releaseChannel')}>
              <option value="stable">stable</option>
              <option value="canary">canary</option>
              <option value="beta">beta</option>
            </select>
          </AccessFormField>
        </div>
        <AccessFormField label="Description" htmlFor="cnd-desc" error={form.formState.errors.description?.message}>
          <input id="cnd-desc" className="input-base w-full" {...form.register('description')} />
        </AccessFormField>
        <div className="mt-4 flex justify-end">
          <button type="button" className="btn-primary" onClick={saveConfig} disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : 'Save configuration'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      {connector.status !== 'decommissioned' && (
        <div className="card border-danger-200">
          <h2 className="mb-2 text-lg font-semibold text-danger-700">Danger zone</h2>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Decommissioning removes this connector from its group's rotation. Flows pinned to it break;
              other connectors in the group keep serving.
            </p>
            <button type="button" className="btn-danger" onClick={() => setDecommissionOpen(true)}>
              Decommission
            </button>
          </div>
        </div>
      )}
      </>
      )}

      <ConfirmDialog
        isOpen={decommissionOpen}
        onClose={() => setDecommissionOpen(false)}
        onConfirm={handleDecommission}
        title="Decommission connector"
        message={`Decommission '${connector.name ?? connector.node_id}'? Flows pinned to it will break and re-establish via surviving connectors in the group.`}
        confirmText="Decommission"
        variant="danger"
        isLoading={decommissioning}
      />
    </div>
  );
};

export default ConnectorDetail;
